<script setup lang="ts">
// NPM
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';

// Components
import { BaseBadge, BaseButton, BaseIcon } from '@magma-app/magma-lapilli';

// Types
import type { PropType } from 'vue';
import type { IUgc } from '@/types/IUgc';

// Helpers
import AnalyticsHelper from '@/helpers/analytics';

// Stores
import { useUgcsStore } from '@/stores/ugcs';
import { useCommonStore } from '@/stores/common';
import { useUserStore } from '@/stores/user';
const ugcsStore = useUgcsStore();
const commonStore = useCommonStore();
const userStore = useUserStore();

// Plugins
const router = useRouter();
const { t } = useI18n({ useScope: 'global' });

// Props
const props = defineProps({
  description: {
    type: String,
    default: null,
  },
  closeCta: {
    type: String,
    default: null,
  },
  open: {
    type: Boolean,
    default: false,
  },
  item: {
    type: Object as PropType<{
      description: string;
      name: string;
      pictureUrl: string;
      points: number;
      status?: string;
    }>,
    default: null,
  },
  role: {
    type: String as PropType<
      'ugc' | 'ugc-awaiting-validation' | 'ugc-completed' | 'ugc-status'
    >,
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
});

// Reactive variables
const root = ref<HTMLInputElement>();
const isSaving = ref(false);

// Computed
const ugcStatus = computed(() => {
  switch (props.item.status) {
    case 'waitingValidation':
      return {
        color: 'yellow',
        text: t('generic.validation.pending'),
      };
    case 'refusedByAdmin':
      return {
        color: 'red',
        text: t('generic.validation.declined'),
      };
    default:
      return {
        color: 'green',
        text: t('generic.validation.completed'),
      };
  }
});

// Methods
const onCloseModal = async () => {
  commonStore.closeManageUgcsModal();

  if (['ugc-awaiting-validation', 'ugc-completed'].includes(props.role)) {
    router.push({ name: 'ugcs' });

    ugcsStore.setUgcDetails(false);
    ugcsStore.setCompletedUgcDetails(false);
  }
};

const onContactAdmin = async () => {
  window.open(
    `mailto:${userStore.user?.organization?.notificationEmail}`,
    '_blank'
  );

  if (
    props.role === 'ugc-status' &&
    (props.item.status === 'waitingValidation' ||
      props.item.status === 'refusedByAdmin')
  ) {
    AnalyticsHelper.trackEvent('ask_support', {
      event_properties: 'ugc',
      user_properties: 'helper',
    });
  }
};

const onStartingUgc = async (ugc: IUgc) => {
  if (isSaving.value) return;
  isSaving.value = true;
  if (ugc.status === 'notSignedUp' && ugc.ugcId) {
    await ugcsStore.startUgc(ugc.ugcId);

    AnalyticsHelper.trackEvent('ugc_doing', {
      user_properties: 'helper',
    });
  }

  if (ugc.ctaHref) {
    if (ugc.ctaCategory === 'email') {
      window.open(`mailto:${ugc.ctaHref}`, '_blank');
    } else {
      window.open(ugc.ctaHref, '_blank');
    }
  }

  commonStore.closeManageUgcsModal();
  isSaving.value = false;
};

const onValidatingUgc = async (ugc: IUgc) => {
  if (isSaving.value) return;
  isSaving.value = true;

  if (ugc.userUgcId) {
    await ugcsStore.accomplishUgc(ugc.userUgcId);

    AnalyticsHelper.trackEvent('ugc_done', {
      user_properties: 'helper',
    });
  }

  const completedUgc = ugcsStore.completedUgcs.find(
    (completedUgc) => completedUgc.userUgcId === ugc.userUgcId
  );

  if (completedUgc) {
    if (completedUgc.status === 'toValidate') {
      commonStore.setManageUgcsModal({
        description: t('manageUgcsModal.completedModal.description'),
        open: true,
        item: ugc,
        role: 'ugc-awaiting-validation',
        title: t('generic.validation.pending'),
      });
    } else if (completedUgc.status === 'validated') {
      commonStore.setManageUgcsModal({
        open: true,
        item: ugc,
        role: 'ugc-completed',
        title: t('generic.completedUgc'),
      });
    }
  }

  isSaving.value = false;
};
</script>

<template>
  <TransitionRoot as="template" :show="open" data-cy="modal">
    <Dialog
      :initial-focus="root"
      as="div"
      class="relative z-10"
      @close="onCloseModal"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-background-emphasis bg-opacity-70 backdrop-blur-md transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div ref="root" class="flex min-h-full items-center justify-center">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="mx-4 transform overflow-hidden rounded-[15px] bg-background-default shadow-xl transition-all md:mx-auto"
              :class="[
                ['ugc', 'ugc-completed', 'ugc-status'].includes(role)
                  ? 'w-[520px]'
                  : 'max-w-sm',
                'p-6'
              ]"
            >
              <div
                v-if="!['ugc', 'ugc-status'].includes(role)"
                class="mb-4 flex justify-between"
              >
                <svg
                  v-if="role === 'ugc-awaiting-validation'"
                  width="56"
                  height="56"
                  viewBox="0 0 56 56"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="4"
                    y="4"
                    width="48"
                    height="48"
                    rx="24"
                    fill="#E0EAFF"
                  />
                  <path
                    d="M28 22V28L32 30M38 28C38 33.5228 33.5228 38 28 38C22.4772 38 18 
										33.5228 18 28C18 22.4772 22.4772 18 28 18C33.5228 18 38 22.4772 38 28Z"
                    stroke="#444CE7"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <rect
                    x="4"
                    y="4"
                    width="48"
                    height="48"
                    rx="24"
                    stroke="#EEF4FF"
                    stroke-width="8"
                  />
                </svg>

                <svg
                  v-else-if="role === 'ugc-completed'"
                  width="56"
                  height="56"
                  viewBox="0 0 56 56"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="4"
                    y="4"
                    width="48"
                    height="48"
                    rx="24"
                    fill="#D1FADF"
                  />
                  <path
                    d="M36 22L25 33L20 28"
                    stroke="#03874C"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <rect
                    x="4"
                    y="4"
                    width="48"
                    height="48"
                    rx="24"
                    stroke="#ECFDF3"
                    stroke-width="8"
                  />
                </svg>

                <BaseIcon
                  icon="close"
                  color="#5F6C85"
                  hover-state
                  @click="onCloseModal"
                />
              </div>

              <div v-if="['ugc', 'ugc-status'].includes(role)" class="relative">
                <div class="relative">
                  <img
                    class="mb-8 h-[280px] w-full rounded-[5px] border border-transparent object-cover"
                    :src="
                      item.pictureUrl !== ''
                        ? item.pictureUrl
                        : '/img/reward.png'
                    "
                  />
                  <span
                    v-if="!item.pictureUrl"
                    class="absolute inset-0 flex items-center justify-center text-mgm-txt-sm font-medium text-foreground-brand-default"
                    >{{ item.points }}</span
                  >
                </div>

                <BaseIcon
                  class="absolute right-4 top-4"
                  bg-color="#E2E8F0"
                  color="#5F6C85"
                  icon="close"
                  hover-state
                  @click="onCloseModal"
                />
              </div>

              <div
                v-if="['ugc', 'ugc-status'].includes(role)"
                class="flex items-start justify-between"
              >
                <h3
                  class="mr-4 max-w-[80%] text-mgm-txt-lg font-medium text-foreground-emphasis"
                >
                  {{ title }}
                </h3>
              </div>

              <h3
                v-else
                class="mb-2 text-mgm-txt-lg font-semibold text-foreground-emphasis"
              >
                {{ title }}
              </h3>

              <BaseBadge
                v-if="role === 'ugc-status'"
                class="my-2"
                size="md"
                :color="ugcStatus.color"
                :text="ugcStatus.text"
              />

              <div
                v-if="
                  role === 'ugc-completed' &&
                  userStore.user?.organization?.pointEnabled &&
                  userStore.user?.balance
                "
                class="mb-6"
              >
                <div
                  class="mb-2 flex justify-between text-mgm-txt-sm font-normal text-foreground-default"
                >
                  <span>{{ $t('manageUgcsModal.purchaseModal.points') }}</span>
                  <span>{{ userStore.user?.balance - item.points }}</span>
                </div>

                <div
                  class="mb-2 flex justify-between text-mgm-txt-sm font-normal text-foreground-default"
                >
                  <span>{{ $t('manageUgcsModal.completedModal.won') }}</span>
                  <span>{{ item.points }}</span>
                </div>

                <div
                  v-if="userStore.user.balance"
                  class="flex justify-between border-t border-border-subtle pt-2 text-mgm-txt-sm font-medium text-foreground-emphasis"
                >
                  <span class="mr-8">{{
                    $t('manageUgcsModal.completedModal.balance')
                  }}</span>
                  <span>{{ userStore.user.balance }}</span>
                </div>
              </div>

              <p
                class="scrollbar-hide max-h-44 overflow-scroll text-mgm-txt-sm font-normal text-foreground-default"
                v-html="description"
              ></p>

              <template v-if="role === 'ugc'">
                <BaseButton
                  class="mb-2 mt-6 w-full"
                  :disabled="item.status === 'ongoing' || isSaving"
                  :text="$t('generic.cta.enroll')"
                  @click="onStartingUgc(item)"
                />

                <BaseButton
                  class="w-full"
                  :disabled="item.status === 'notSignedUp' || isSaving"
                  :text="$t('generic.done')"
                  @click="onValidatingUgc(item)"
                />
              </template>

              <BaseButton
                v-if="
                  role === 'ugc-status' &&
                  (item.status === 'waitingValidation' ||
                    item.status === 'refusedByAdmin')
                "
                :text="$t('generic.adminContact')"
                class="mt-6 w-full"
                size="md"
                state="scdr"
                @click="onContactAdmin"
              />

              <BaseButton
                v-if="
                  ['ugc-awaiting-validation', 'ugc-completed'].includes(role) ||
                  closeCta
                "
                class="w-full mt-6"
                state="prmr"
                :text="
                  role === 'ugc-completed'
                    ? $t('manageUgcsModal.completedModal.thanks')
                    : closeCta
                      ? closeCta
                      : $t('generic.doneModalCta')
                "
                data-cy="btn-close-modal"
                @click="onCloseModal"
              />
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
